import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Link, TextInput, FormControl } from "@primer/react";
import { CheckIcon, DownloadIcon, UploadIcon, XIcon, SyncIcon } from "@primer/octicons-react";
import { uploadApi } from '../../../api/uploadApi';
import { ketQuaThiDauVaoApi } from '../../../api/ketQuaThiDauVaoApi';
import { appInfo } from '../../../AppInfo';
import { AnimationPopup } from '../../../components/modal';
import DataTable from '../../../components/data-table/DataTable';
import { PopUpFormActions } from '../../../components/pop-up-form/PopUpForm';
import Steps, { IStepData } from '../../../components/steps/Steps';
import Button from '../../../components/ui/button';
import { useCommonContext } from '../../../contexts/common';
import { NotifyHelper } from '../../../helpers/toast';
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import * as XLSX from 'xlsx';
import { crmHocSinhBHYTApi } from "../../../api/crmHocSinhBHYTApi";

interface IImportBHYTModalProps {
    onClose: () => void;
    animationOf: string;
}

const _steps: IStepData[] = [
    {
        id: 1,
        name: "Upload file",
        is_active: true
    },
    {
        id: 2,
        name: "Validate",
        is_active: false
    },
    {
        id: 3,
        name: "Import",
        is_active: false
    },
    {
        id: 4,
        name: "Result",
        is_active: false
    }
];

const ImportBHYTModal = (props: IImportBHYTModalProps) => {
    const { translate } = useCommonContext();
    const { onClose, animationOf } = props;

    const [stepId, setStepId] = useState(1);
    const [uploadedFileName, setUploadedFileName] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const componentDidMount = useRef<boolean>(false);
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [dataSourceValidated, setDataSourceValidated] = useState<any[]>([]);
    const [filterStatus, setFilterStatus] = useState<string>('all'); // 'all', 'valid', 'error'
    const [importResult, setImportResult] = useState<any>(null);
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

    const statistics = useMemo(() => {
        const total = dataSourceValidated.length;
        const errorCount = dataSourceValidated.filter((x) => x.err && x.err.trim() !== "").length;
        const validCount = total - errorCount;
        return { total, validCount, errorCount };
    }, [dataSourceValidated]);

    const filteredData = useMemo(() => {
        if (filterStatus === 'valid') {
            return dataSourceValidated.filter((x) => !x.err || x.err.trim() === "");
        } else if (filterStatus === 'error') {
            return dataSourceValidated.filter((x) => x.err && x.err.trim() !== "");
        }
        return dataSourceValidated;
    }, [dataSourceValidated, filterStatus]);

    useEffect(() => {
        componentDidMount.current = true;
        return () => {
            componentDidMount.current = false;
        };
    }, []);

    const handleSelectFile = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute(
            'accept',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
        );
        input.click();
        input.onchange = async () => {
            var file = input && input.files ? input.files[0] : null;
            if (file) {
                setIsLoading(true);
                const res = await uploadApi.uploadExcel(file);
                if (res.is_success) {
                    setUploadedFileName(res.data.fileNameId);
                    setStepId(2);
                } else {
                    NotifyHelper.Error(res.message ?? "Error");
                }
                setIsLoading(false);
            }
        };
        input.remove();
    };

    useEffect(() => {
        if (stepId === 2 && uploadedFileName) {
            handleReadUploadedFile();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepId, uploadedFileName]);

    const handleReadUploadedFile = async () => {
        setIsLoading(true);
        const res = await uploadApi.readExcel({
            fileName: uploadedFileName,
            sheetIndex: 0,
        });

        if (!componentDidMount.current) return;

        if (res.is_success) {
            setDataSource(res.data);
            await handleValidateImportFile();
        }
        setIsLoading(false);
    };

    const handleValidateImportFile = async () => {
      setIsLoading(true);
      const datasend = {
          fileName: uploadedFileName,
          sheetIndex: 0
      };
      const res = await crmHocSinhBHYTApi.validateImport(datasend, nam_hoc, dm_coso_id);
      if (!componentDidMount.current) return;
      if (res.is_success) {
          setDataSourceValidated(res.data);
      }
      setIsLoading(false);
    };

    const handleImport = async () => {
        setIsLoading(true);
        setStepId(3); // Chuyển sang bước 3 (Import/Processing)

        // Calculate statistics
        const statistics = {
            total: dataSourceValidated.length,
            validCount: dataSourceValidated.filter(x => !x.err || x.err === "").length,
            errorCount: dataSourceValidated.filter(x => x.err && x.err !== "").length
        };

        const datasend = {
            fileName: uploadedFileName,
            sheetIndex: 0
        };

        const res = await crmHocSinhBHYTApi.import(datasend, nam_hoc, dm_coso_id);
        if (!componentDidMount.current) return;

        if (res.is_success) {
            // Lưu kết quả import để hiển thị ở bước 4
            setImportResult({
                total: statistics.total,
                success: statistics.validCount, // Số dòng hợp lệ
                error: statistics.errorCount,
                data: dataSourceValidated
            });
            setStepId(4); // Chuyển sang bước 4 (Done)
            NotifyHelper.Success(`Import hoàn tất! ${statistics.validCount} dòng hợp lệ đã được import.`);
        } else {
            NotifyHelper.Error("Import thất bại: " + (res.message || "Lỗi không xác định"));
            setStepId(2); // Quay lại bước 2 nếu lỗi
        }
        setIsLoading(false);
    };

    // Filter data based on status
    const getFilteredData = () => {
        if (filterStatus === 'valid') {
            return dataSourceValidated.filter(x => !x.err || x.err === "");
        } else if (filterStatus === 'error') {
            return dataSourceValidated.filter(x => x.err && x.err !== "");
        }
        return dataSourceValidated;
    };

    // Download functions
    const downloadExcel = (data: any[], filename: string) => {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        XLSX.writeFile(wb, filename);
    };

    const handleDownloadAll = () => {
        if (importResult && importResult.data) {
            downloadExcel(importResult.data, `import_bhyt_all_${new Date().getTime()}.xlsx`);
        }
    };

    const handleDownloadSuccess = () => {
        if (importResult && importResult.data) {
            const successData = importResult.data.filter((x: any) => !x.err || x.err === "");
            downloadExcel(successData, `import_bhyt_success_${new Date().getTime()}.xlsx`);
        }
    };

    const handleDownloadErrors = () => {
        if (importResult && importResult.data) {
            const errorData = importResult.data.filter((x: any) => x.err && x.err !== "");
            downloadExcel(errorData, `import_bhyt_errors_${new Date().getTime()}.xlsx`);
        }
    };

    return (
        <AnimationPopup
            animationOf={animationOf}
            width="70%"  // Set width to 100% - this prop seems to be accepted
            title='Import học sinh BHYT'
            onClose={onClose}
        >
            <Box style={{
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 220px)",
                overflow: "hidden"
            }}>
                <Box style={{ marginBottom: "24px" }}>
                    <Steps steps={_steps.map(x => ({
                        ...x,
                        is_active: x.id === stepId
                    }))} />
                </Box>

                <Box style={{ display: "flex", marginBottom: "24px" }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                        <Button
                            leadingVisual={UploadIcon}
                            text='Upload file'
                            onClick={handleSelectFile}
                            size='medium'
                            isLoading={isLoading}
                        />
                        {uploadedFileName && (
                            <TextInput
                                style={{ width: 400 }}
                                value={uploadedFileName}
                                readOnly
                            />
                        )}
                        <Box style={{ flex: 1 }} />
                        <Link href={`${appInfo.baseApiURL.replace('/api', '')}/TEMPLATE/IMPORT_BHYT.xlsx`} target='_blank'>
                            <Button
                                text={translate('Tải mẫu import bhyt vào tại đây')}
                                variant='invisible'
                                leadingVisual={DownloadIcon}
                            />
                        </Link>
                    </div>
                </Box>

                {stepId === 2 && (
                    <Box style={{ flex: 1, overflow: "auto" }}>
                        <Box style={{ marginBottom: "16px", display: "flex", gap: "16px", alignItems: "center", justifyContent: "space-between" }}>
                            <Box>
                                <label style={{ marginRight: "8px" }}>Lọc:</label>
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #ccc" }}
                                >
                                    <option value="all">Tất cả ({dataSourceValidated.length})</option>
                                    <option value="valid">Hợp lệ ({dataSourceValidated.filter(x => !x.err || x.err === "").length})</option>
                                    <option value="error">Lỗi ({dataSourceValidated.filter(x => x.err && x.err !== "").length})</option>
                                </select>
                            </Box>
                            <Box style={{ display: "flex", gap: "8px" }}>
                                <Button
                                    text={`Tải tất cả (${dataSourceValidated.length})`}
                                    variant="invisible"
                                    size="small"
                                    leadingVisual={DownloadIcon}
                                    onClick={() => downloadExcel(dataSourceValidated, `validate_bhyt_all_${new Date().getTime()}.xlsx`)}
                                />
                                <Button
                                    text={`Tải hợp lệ (${dataSourceValidated.filter(x => !x.err || x.err === "").length})`}
                                    variant="invisible"
                                    size="small"
                                    leadingVisual={DownloadIcon}
                                    onClick={() => downloadExcel(dataSourceValidated.filter(x => !x.err || x.err === ""), `validate_bhyt_valid_${new Date().getTime()}.xlsx`)}
                                />
                                <Button
                                    text={`Tải lỗi (${dataSourceValidated.filter(x => x.err && x.err !== "").length})`}
                                    variant="invisible"
                                    size="small"
                                    leadingVisual={DownloadIcon}
                                    onClick={() => downloadExcel(dataSourceValidated.filter(x => x.err && x.err !== ""), `validate_bhyt_errors_${new Date().getTime()}.xlsx`)}
                                />
                            </Box>
                        </Box>
                        <DataTable
                            columns={[
                                {
                                    field: "status",
                                    header: "",
                                    width: "40px",
                                    renderCell: (data: any) => (
                                        <Box sx={{
                                            color: data.err && data.err !== "" ? 'danger.fg' : 'success.fg',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {data.err && data.err !== "" ? "❌" : "✅"}
                                        </Box>
                                    )
                                },
                                { field: "Mã học sinh", header: "Mã học sinh", width: "120px" },
                                { field: "Tình trạng sức khỏe", header: "Tình trạng sức khỏe", width: "80px" },
                                { field: "Số định danh", header: "Số định danh", width: "120px" },
                                { field: "Số thẻ BHYT Học sinh", header: "Số thẻ BHYT Học sinh", width: "150px" },
                                { field: "Nơi đăng ký khai sinh", header: "Nơi đăng ký khai sinh", width: "150px" },
                                { field: "Con của LLVT - Bộ đội, Công an", header: "Con của LLVT - Bộ đội, Công an", width: "100px" },
                                { field: "Ghi chú", header: "Ghi chú", width: "100px" },
                                { field: "err", header: "Lỗi", width: "300px" }
                            ]}
                            data={getFilteredData()}
                            height="400px"
                        />
                    </Box>
                )}

                {stepId === 3 && (
                    <Box style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "16px"
                    }}>
                        <div style={{
                            animation: "spin 1s linear infinite"
                        }}>
                            <SyncIcon size={48} />
                        </div>
                        <h3>Đang xử lý import dữ liệu...</h3>
                        <p>Vui lòng đợi trong giây lát</p>
                        <style>{`
                            @keyframes spin {
                                from { transform: rotate(0deg); }
                                to { transform: rotate(360deg); }
                            }
                        `}</style>
                    </Box>
                )}

                {stepId === 4 && importResult && (
                    <Box style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        padding: "12px"
                    }}>
                        {/* Header và Statistics trong một hàng */}
                        <Box style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "16px",
                            padding: "12px 16px",
                            backgroundColor: "#f6f8fa",
                            borderRadius: "6px",
                            border: "1px solid #e1e4e8"
                        }}>
                            {/* Success Icon và Text */}
                            <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <div style={{
                                    width: "40px",
                                    height: "40px",
                                    backgroundColor: "#1a7f37",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <CheckIcon size={24} fill="white" />
                                </div>
                                <Box>
                                    <div style={{ fontSize: "16px", fontWeight: "600", color: "#1a7f37" }}>
                                        Import hoàn tất!
                                    </div>
                                    <div style={{ fontSize: "12px", color: "#656d76" }}>
                                        Dữ liệu bhyt đầu vào đã được xử lý
                                    </div>
                                </Box>
                            </Box>

                            {/* Statistics */}
                            <Box style={{ display: "flex", gap: "24px", alignItems: "center" }}>
                                <Box style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#0969da" }}>
                                        {importResult.total}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#656d76" }}>Tổng số</div>
                                </Box>
                                <Box style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#1a7f37" }}>
                                        {importResult.success}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#656d76" }}>Thành công</div>
                                </Box>
                                <Box style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#d1242f" }}>
                                        {importResult.error}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#656d76" }}>Lỗi</div>
                                </Box>
                            </Box>
                        </Box>

                        {/* Download Buttons */}
                        <Box style={{
                            display: "flex",
                            gap: "8px",
                            marginBottom: "12px",
                            padding: "8px 12px",
                            backgroundColor: "#f6f8fa",
                            borderRadius: "4px",
                            border: "1px solid #e1e4e8"
                        }}>
                            <Button
                                text={`Tải tất cả (${importResult.total})`}
                                variant="invisible"
                                size="small"
                                leadingVisual={DownloadIcon}
                                onClick={handleDownloadAll}
                            />
                            <Button
                                text={`Tải thành công (${importResult.success})`}
                                variant="invisible"
                                size="small"
                                leadingVisual={DownloadIcon}
                                onClick={handleDownloadSuccess}
                            />
                            <Button
                                text={`Tải lỗi (${importResult.error})`}
                                variant="invisible"
                                size="small"
                                leadingVisual={DownloadIcon}
                                onClick={handleDownloadErrors}
                            />
                        </Box>

                        {/* Data Table - Compact */}
                        <Box style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            minHeight: "250px"
                        }}>
                            {/* Filter Section - Inline */}
                            <Box style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px',
                                padding: '4px 8px',
                                backgroundColor: '#f6f8fa',
                                borderRadius: '3px',
                                border: '1px solid #e1e4e8'
                            }}>
                                <Box style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '500' }}>
                                        Tổng: <span style={{ color: '#0969da', fontWeight: '600' }}>{statistics.total}</span>
                                    </span>
                                    <span style={{ fontSize: '11px', fontWeight: '500' }}>
                                        OK: <span style={{ color: '#1a7f37', fontWeight: '600' }}>{statistics.validCount}</span>
                                    </span>
                                    <span style={{ fontSize: '11px', fontWeight: '500' }}>
                                        Lỗi: <span style={{ color: '#d1242f', fontWeight: '600' }}>{statistics.errorCount}</span>
                                    </span>
                                </Box>
                                <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '500' }}>Lọc:</span>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        style={{
                                            fontSize: '11px',
                                            padding: '2px 6px',
                                            borderRadius: '3px',
                                            border: '1px solid #d1d9e0',
                                            backgroundColor: 'white'
                                        }}
                                    >
                                        <option value="all">Tất cả</option>
                                        <option value="valid">Hợp lệ</option>
                                        <option value="error">Lỗi</option>
                                    </select>
                                </Box>
                            </Box>

                            {/* Table Container */}
                            <Box style={{
                                flex: 1,
                                border: '1px solid #e1e4e8',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                backgroundColor: 'white'
                            }}>
                                <div style={{
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    overflowX: 'auto'
                                }}>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '12px'
                                    }}>
                                        <thead style={{
                                            backgroundColor: '#f6f8fa',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1
                                        }}>
                                            <tr>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    textAlign: 'left',
                                                    fontWeight: '600',
                                                    width: '120px',
                                                    minWidth: '120px'
                                                }}>Kết quả</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    textAlign: 'left',
                                                    fontWeight: '600',
                                                    width: '120px'
                                                }}>Mã học sinh</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    textAlign: 'left',
                                                    fontWeight: '600',
                                                    width: '80px'
                                                }}>Tình trạng sức khỏe</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    textAlign: 'left',
                                                    fontWeight: '600',
                                                    width: '120px'
                                                }}>Số định danh</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    textAlign: 'left',
                                                    fontWeight: '600',
                                                    width: '150px'
                                                }}>Số thẻ BHYT Học sinh</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    textAlign: 'left',
                                                    fontWeight: '600',
                                                    width: '150px'
                                                }}>Nơi đăng ký khai sinh</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    textAlign: 'left',
                                                    fontWeight: '600',
                                                    width: '100px'
                                                }}>Con của LLVT - Bộ đội, Công an</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    textAlign: 'left',
                                                    fontWeight: '600',
                                                    width: '100px'
                                                }}>Ghi chú</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((row, rowIndex) => (
                                                <tr key={rowIndex} style={{
                                                    backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f6f8fa"
                                                }}>
                                                    {/* Cột kết quả */}
                                                    <td style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: '1px solid #e1e4e8',
                                                        verticalAlign: 'top'
                                                    }}>
                                                        {row.err ? (
                                                            <FormControl.Validation variant='error'>
                                                                <div style={{
                                                                    whiteSpace: 'pre-line',
                                                                    fontSize: '11px',
                                                                    lineHeight: '1.3'
                                                                }}>
                                                                    {row.err}
                                                                </div>
                                                            </FormControl.Validation>
                                                        ) : (
                                                            <FormControl.Validation variant='success'>
                                                                <span style={{ fontSize: '11px' }}>✓ Hợp lệ</span>
                                                            </FormControl.Validation>
                                                        )}
                                                    </td>
                                                    <td style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: '1px solid #e1e4e8',
                                                        verticalAlign: 'top'
                                                    }}>{row["Mã học sinh"] || ""}</td>
                                                    <td style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: '1px solid #e1e4e8',
                                                        verticalAlign: 'top'
                                                    }}>{row["Tình trạng sức khỏe"] || ""}</td>
                                                    <td style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: '1px solid #e1e4e8',
                                                        verticalAlign: 'top'
                                                    }}>{row["Số định danh"] || ""}</td>
                                                    <td style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: '1px solid #e1e4e8',
                                                        verticalAlign: 'top'
                                                    }}>{row["Số thẻ BHYT Học sinh"] || ""}</td>
                                                    <td style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: '1px solid #e1e4e8',
                                                        verticalAlign: 'top'
                                                    }}>{row["Nơi đăng ký khai sinh"] || ""}</td>
                                                    <td style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: '1px solid #e1e4e8',
                                                        verticalAlign: 'top'
                                                    }}>{row["Con của LLVT - Bộ đội, Công an"] || ""}</td>
                                                    <td style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: '1px solid #e1e4e8',
                                                        verticalAlign: 'top'
                                                    }}>{row["Ghi chú"] || ""}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Box>
                        </Box>
                    </Box>
                )}

                <div style={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    padding: '16px 0',
                    borderTop: '1px solid #e1e4e8'
                }}>
                    <PopUpFormActions>
                        <Button
                            leadingVisual={XIcon}
                            text='Base.Label.Close'
                            onClick={onClose}
                        />
                        {stepId === 2 && (
                            <Button
                                leadingVisual={CheckIcon}
                                variant='primary'
                                isLoading={isLoading}
                                text={'ThoiKhoaBieuImport.Label.Save'}
                                onClick={handleImport}
                            />
                        )}
                        {stepId === 4 && (
                            <Button
                                leadingVisual={CheckIcon}
                                variant='primary'
                                text='Hoàn tất'
                                onClick={onClose}
                            />
                        )}
                    </PopUpFormActions>
                </div>
            </Box>
        </AnimationPopup>
    );
};

export default ImportBHYTModal;
